import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import { fr } from "yup-locales";
import { setLocale } from "yup";
import { cleanerError } from "@/lib/utils";
import {
  Restaurant,
  RestaurantFormData,
} from "@/redux/services/restaurant/restaurant.type";
import { useUpdateRestaurantMutation } from "@/redux/services/restaurant/restaurant.api";
import { useAppDispatch } from "@/redux/features/hooks";
import { setCurrentRestaurant } from "@/redux/features/user/user.slice";
import { useUploadMedia } from "@/lib/useUploadMedia";
import { toastApiError } from "@/lib/apiErrors";

setLocale(fr);

const schema = yup.object().shape({
  name: yup.string().required().label("Nom"),
  address: yup.string().required().label("Adresse"),
  shouldAskForWaiterName: yup.boolean().label("Demander le nom du serveur"),
  settings: yup.object().shape({
    orderNumberingStrategy: yup.object().shape({
      type: yup.string().label("Type"),
      prefix: yup.string().label("Préfixe"),
      padding: yup.number().min(2).label("Padding").default(2),
      startNumber: yup.number().min(1).label("Numéro de départ").default(1),
      dateFormat: yup.string().label("Format de date"),
      resetFrequency: yup.string().label("Fréquence de réinitialisation"),
    }),
    purchaseOrderNumbering: yup.object().shape({
      prefix: yup.string().label("Préfixe"),
      padding: yup.number().min(1).label("Zéros devant").default(6),
    }),
    shouldAskForWaiterName: yup.boolean().label("Demander le nom du serveur"),
    shouldAskForGuestCount: yup
      .boolean()
      .label("Demander le nombre de couverts"),
    allowOrderPlacement: yup.boolean().label("Autoriser la prise de commande"),
    collectCustomerDataUpfront: yup
      .boolean()
      .label("Collecter les données client en amont"),
    trackIngredientStock: yup
      .boolean()
      .label("Suivi automatique du stock"),
  }),
});

export const useEditRestaurant = (item?: Restaurant) => {
  const dispatch = useAppDispatch();
  const [shouldAskForWaiterName, setShouldAskForWaiterName] = useState(
    item?.shouldAskForWaiterName
  );
  const [allowOrderPlacement, setAllowOrderPlacement] = useState(
    item?.settings?.allowOrderPlacement ?? true
  );
  const [collectCustomerDataUpfront, setCollectCustomerDataUpfront] = useState(
    item?.settings?.collectCustomerDataUpfront ?? false
  );
  const [shouldAskForGuestCount, setShouldAskForGuestCount] = useState(
    item?.settings?.shouldAskForGuestCount ?? false
  );
  const [trackIngredientStock, setTrackIngredientStock] = useState(
    item?.settings?.trackIngredientStock ?? true
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
    watch,
    setValue,
  } = useForm<RestaurantFormData>({
    // @ts-ignore
    resolver: yupResolver(schema),
  });

  const [update, { isLoading }] = useUpdateRestaurantMutation();

  useEffect(() => {
    if (errors) {
      cleanerError(errors, clearErrors);
    }
  }, [errors, clearErrors]);

  useEffect(() => {
    if (item) {
      setShouldAskForWaiterName(item.settings?.shouldAskForWaiterName);
      setAllowOrderPlacement(item.settings?.allowOrderPlacement ?? true);
      setCollectCustomerDataUpfront(
        item.settings?.collectCustomerDataUpfront ?? false
      );
      setShouldAskForGuestCount(item.settings?.shouldAskForGuestCount ?? false);
      setTrackIngredientStock(item.settings?.trackIngredientStock ?? true);
    }
  }, [item]);

  const handleShouldAskForWaiterName = (checked: boolean) => {
    setShouldAskForWaiterName(checked);
    setValue("settings.shouldAskForWaiterName", checked);
  };

  const handleAllowOrderPlacement = (checked: boolean) => {
    setAllowOrderPlacement(checked);
    setValue("settings.allowOrderPlacement", checked);
  };

  const handleCollectCustomerDataUpfront = (checked: boolean) => {
    setCollectCustomerDataUpfront(checked);
    setValue("settings.collectCustomerDataUpfront", checked);
  };

  const handleShouldAskForGuestCount = (checked: boolean) => {
    setShouldAskForGuestCount(checked);
    setValue("settings.shouldAskForGuestCount", checked);
  };

  const handleTrackIngredientStock = (checked: boolean) => {
    setTrackIngredientStock(checked);
    setValue("settings.trackIngredientStock", checked);
  };

  const onSubmit = async (data: RestaurantFormData) => {
    const res = await update({ id: item?.id as string, data: data });

    if ("data" in res) {
      toast.success(`Informations mis à jour avec succès`);
      reset();

      dispatch(setCurrentRestaurant(res.data as Restaurant));
    } else if ("error" in res) {
      toastApiError(res.error, "Une erreur inconnue est survenue");
    }
  };

  return {
    register,
    errors,
    onSubmit: handleSubmit(onSubmit),
    isLoading,
    watch,
    handleShouldAskForWaiterName,
    shouldAskForWaiterName,
    handleAllowOrderPlacement,
    allowOrderPlacement,
    handleCollectCustomerDataUpfront,
    collectCustomerDataUpfront,
    handleShouldAskForGuestCount,
    shouldAskForGuestCount,
    handleTrackIngredientStock,
    trackIngredientStock,
  };
};

export const useUpdateRestaurantLogo = (item: Restaurant) => {
  const [avatar, setAvatar] = useState<string>();
  const [update] = useUpdateRestaurantMutation();
  const { uploadMedia } = useUploadMedia();
  const dispatch = useAppDispatch();

  const handleUpdateLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files[0];
      setAvatar(URL.createObjectURL(file));
      const media = await uploadMedia(file);
      if (media && "id" in media) {
        const data = {
          newImageUrl: media?.url,
          image: `/api/media_objects/${media?.id}`,
        };

        const res = await update({
          id: item?.id,
          data: data as RestaurantFormData,
        });

        if ("data" in res) {
          dispatch(setCurrentRestaurant(res.data as Restaurant));
          toast.success("Logo mis à jour avec succès !");
        }
      }
    }
  };

  return {
    handleUpdateLogo,
    avatar,
  };
};
